import { FC, useContext, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useClaimAttachmentsApi } from "../../http/claimAttachments/api";
import { isConflict } from "../../http/errors";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import FileUploader from "../ui/FileUploader";
import ModalDialog from "../ui/ModalDialog";

interface UploadClaimAttachmentProps {
  claimId: string;
}

const UploadClaimAttachment: FC<UploadClaimAttachmentProps> = ({ claimId }) => {
  const i18n = useI18n();
  const [alreadyPresent, setAlreadyPresent] = useState<File>();
  const [isModalOpen, setModalOpen] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const claimAttachmentsApi = useClaimAttachmentsApi();

  const onSubmit = async (file: File, overwrite?: boolean): Promise<void> => {
    setIsSubmitting(true);

    try {
      await claimAttachmentsApi.insertClaimAttachment(file, overwrite, claimId);
      setModalOpen(false);
      setIsSubmitting(false);
      setAlreadyPresent(undefined);
      dispatch(showSuccessNotification(i18n.t));
    } catch (err) {
      if (!overwrite && isConflict(err)) {
        setIsSubmitting(false);
        setAlreadyPresent(file);
        return;
      }
      dispatch(showErrorNotification(i18n, err));
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        glyph={SvgAdd}
        buttonProps={{
          onClick: () => setModalOpen(true),
        }}
      />
      {!!isModalOpen && (
        <ModalDialog
          title={i18n.t.documents.add}
          onClose={() => {
            setModalOpen(false);
            setIsSubmitting(false);
          }}
        >
          <FileUploader
            onCancel={() => {
              setModalOpen(false);
              setIsSubmitting(false);
            }}
            onSave={onSubmit}
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
          />
        </ModalDialog>
      )}
      {!!alreadyPresent && (
        <ModalDialog
          title={i18n.t.documents.alreadyPresent}
          isSubmitting={isSubmitting}
          onClose={() => {
            setIsSubmitting(false);
            setAlreadyPresent(undefined);
          }}
          actions={[
            {
              title: i18n.t.common.cancel,
              disabled: isSubmitting,
              onClick: () => setAlreadyPresent(undefined),
              type: "secondary",
            },
            {
              title: i18n.t.common.overwrite,
              disabled: isSubmitting,
              onClick: () => alreadyPresent && onSubmit(alreadyPresent, true),
            },
          ]}
        >
          {i18n.t.documents.questions.overwrite}
        </ModalDialog>
      )}
    </>
  );
};

export default UploadClaimAttachment;
